import { useState } from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { farmsApi } from "../api/farmsApi"
import { queryKeys, queryClient } from "@/shared/api/queryClient"
import { useFarmStore } from "../stores/farmStore"
import { Card } from "@/shared/components/ui/Card"
import { Button } from "@/shared/components/ui/Button"
import { formatDate } from "@/shared/lib/utils"
import { cn } from "@/shared/lib/utils"
import { buildRoute } from "@/shared/lib/constants"

export function FarmsPage() {
  const navigate = useNavigate()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newFarmName, setNewFarmName] = useState("")
  const [newFarmLocation, setNewFarmLocation] = useState("")
  const [newFarmLatitude, setNewFarmLatitude] = useState("")
  const [newFarmLongitude, setNewFarmLongitude] = useState("")
  const [newFarmType, setNewFarmType] = useState<'aquaculture' | 'fish_farming' | 'shrimp_farming' | 'mixed'>('aquaculture')
  const { selectedFarm, setSelectedFarm } = useFarmStore()

  const { data: farms, isLoading } = useQuery({
    queryKey: queryKeys.farms.all(),
    queryFn: farmsApi.list,
  })

  const createMutation = useMutation({
    mutationFn: farmsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.farms.all() })
      setIsCreateDialogOpen(false)
      setNewFarmName("")
      setNewFarmLocation("")
      setNewFarmLatitude("")
      setNewFarmLongitude("")
      setNewFarmType('aquaculture')
    },
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    const latitude = newFarmLatitude ? parseFloat(newFarmLatitude) : undefined
    const longitude = newFarmLongitude ? parseFloat(newFarmLongitude) : undefined
    createMutation.mutate({
      name: newFarmName,
      location: newFarmLocation || undefined,
      latitude: latitude,
      longitude: longitude,
      farmType: newFarmType,
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-roboto text-3xl font-bold text-gray-900">Farms</h1>
          <p className="font-roboto mt-2 text-base text-gray-600">
            Manage your aquaculture farms
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Create Farm
        </Button>
      </div>

      {/* Farms Grid */}
      {farms && farms.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {farms.map((farm) => (
            <div
              key={farm.id}
              onClick={() => {
                setSelectedFarm(farm)
                navigate(buildRoute.farm(farm.id))
              }}
              className={cn(
                "group relative cursor-pointer rounded-xl border bg-white p-6 shadow-sm transition-all duration-200",
                "hover:shadow-md hover:-translate-y-0.5",
                selectedFarm?.id === farm.id
                  ? "border-primary-500 ring-2 ring-primary-500/20 shadow-md"
                  : "border-gray-200 hover:border-primary-300"
              )}
            >
              {/* Selected indicator */}
              {selectedFarm?.id === farm.id && (
                <div className="absolute top-4 right-4">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 shadow-sm">
                    <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={cn(
                  "flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br shadow-sm transition-all duration-200",
                  selectedFarm?.id === farm.id
                    ? "from-primary-500 to-primary-700 shadow-primary-500/30 group-hover:shadow-md group-hover:shadow-primary-500/40"
                    : "from-gray-100 to-gray-200 text-gray-600 group-hover:from-primary-50 group-hover:to-primary-100 group-hover:text-primary-600"
                )}>
                  <svg
                    className={cn(
                      "h-7 w-7 transition-transform duration-200 group-hover:scale-110",
                      selectedFarm?.id === farm.id ? "text-white" : ""
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-roboto text-lg font-semibold text-gray-900 truncate group-hover:text-primary-700 transition-colors">
                    {farm.name}
                  </h3>
                  <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-600">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="font-roboto truncate">
                      {farm.location || "No location"}
                    </p>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5">
                    <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="font-roboto text-xs text-gray-500">
                      Created {formatDate(farm.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Hover effect overlay */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary-500/0 to-primary-500/0 opacity-0 transition-opacity duration-200 group-hover:from-primary-500/5 group-hover:to-primary-500/10 group-hover:opacity-100 pointer-events-none" />
            </div>
          ))}
        </div>
      ) : (
        <Card className="border-dashed border-2">
          <div className="text-center py-16">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100 border border-gray-200 mb-4">
              <svg
                className="h-8 w-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <h3 className="font-roboto text-base font-semibold text-gray-900">No farms yet</h3>
            <p className="font-roboto mt-2 text-sm text-gray-600 max-w-sm mx-auto">
              Get started by creating your first farm to begin managing your aquaculture operations.
            </p>
            <div className="mt-6">
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create Your First Farm
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Create Farm Dialog */}
      {isCreateDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsCreateDialogOpen(false)
          }}
        >
          <div
            className="w-full max-w-md glass-card p-8 shadow-2xl"
            style={{ animation: 'scaleIn 0.2s ease-out' }}
          >
            <div className="mb-8 flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 shadow-lg flex-shrink-0">
                <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div>
                <h2 className="font-roboto text-2xl font-bold text-gray-900">
                  Create New Farm
                </h2>
                <p className="font-roboto mt-1.5 text-sm text-gray-600">
                  Add a new farm to your aquaculture operations
                </p>
              </div>
            </div>

            <form onSubmit={handleCreate} className="space-y-5">
              <div>
                <label className="font-roboto mb-2 block text-sm font-medium text-gray-900">
                  Farm Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newFarmName}
                  onChange={(e) => setNewFarmName(e.target.value)}
                  required
                  autoFocus
                  style={{ border: '1px solid #BDBDBD' }}
                  className={cn(
                    "font-roboto w-full rounded-lg bg-white px-4 py-3",
                    "text-gray-900 placeholder-gray-400",
                    "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20",
                    "transition-all duration-200"
                  )}
                  placeholder="e.g., Coastal Salmon Farm"
                />
              </div>

              <div>
                <label className="font-roboto mb-2 block text-sm font-medium text-gray-900">
                  Farm Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={newFarmType}
                  onChange={(e) => setNewFarmType(e.target.value as 'aquaculture' | 'fish_farming' | 'shrimp_farming' | 'mixed')}
                  required
                  style={{ border: '1px solid #BDBDBD' }}
                  className={cn(
                    "font-roboto w-full rounded-lg bg-white px-4 py-3",
                    "text-gray-900",
                    "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20",
                    "transition-all duration-200"
                  )}
                >
                  <option value="aquaculture">Aquaculture</option>
                  <option value="fish_farming">Fish Farming</option>
                  <option value="shrimp_farming">Shrimp Farming</option>
                  <option value="mixed">Mixed</option>
                </select>
              </div>

              <div>
                <label className="font-roboto mb-2 block text-sm font-medium text-gray-900">
                  Location
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={newFarmLocation}
                    onChange={(e) => setNewFarmLocation(e.target.value)}
                    style={{ border: '1px solid #BDBDBD' }}
                    className={cn(
                      "font-roboto w-full rounded-lg bg-white pl-11 pr-4 py-3",
                      "text-gray-900 placeholder-gray-400",
                      "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20",
                      "transition-all duration-200"
                    )}
                    placeholder="e.g., Pacific Northwest"
                  />
                </div>
              </div>

              {/* GPS Coordinates */}
              <div>
                <label className="font-roboto mb-2 block text-sm font-medium text-gray-900">
                  GPS Coordinates
                </label>
                <p className="font-roboto text-xs text-gray-500 mb-2">
                  Used for automatic weather data retrieval
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <input
                      type="number"
                      step="any"
                      value={newFarmLatitude}
                      onChange={(e) => setNewFarmLatitude(e.target.value)}
                      style={{ border: '1px solid #BDBDBD' }}
                      className={cn(
                        "font-roboto w-full rounded-lg bg-white px-4 py-3",
                        "text-gray-900 placeholder-gray-400",
                        "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20",
                        "transition-all duration-200"
                      )}
                      placeholder="Latitude (e.g., -33.8688)"
                      min="-90"
                      max="90"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      step="any"
                      value={newFarmLongitude}
                      onChange={(e) => setNewFarmLongitude(e.target.value)}
                      style={{ border: '1px solid #BDBDBD' }}
                      className={cn(
                        "font-roboto w-full rounded-lg bg-white px-4 py-3",
                        "text-gray-900 placeholder-gray-400",
                        "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20",
                        "transition-all duration-200"
                      )}
                      placeholder="Longitude (e.g., 151.2093)"
                      min="-180"
                      max="180"
                    />
                  </div>
                </div>
              </div>

              {createMutation.isError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-roboto text-sm font-medium text-red-900">
                      Failed to create farm
                    </p>
                    <p className="font-roboto text-sm text-red-700 mt-0.5">
                      Please try again or contact support if the issue persists.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1"
                >
                  {createMutation.isPending ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Create Farm
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
